import React from 'react';
import { Box, styled } from '@planne-software/uni/mui/material';
import type { TariffCardLayout } from '.';

const headerByLayout = (layout: TariffCardLayout): React.CSSProperties => {
  switch (layout) {
    case 'mobile':
      return { flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' };
    case 'desktop':
      return { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' };
  }
};

const priceRowByLayout = (layout: TariffCardLayout): React.CSSProperties => {
  switch (layout) {
    case 'mobile':
      return { width: '100%', justifyContent: 'space-between' };
    case 'desktop':
      return { width: 'auto', justifyContent: 'flex-end' };
  }
};

export const CardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasError' && prop !== 'dimmed',
})<{ hasError: boolean; dimmed: boolean }>(({ theme, hasError, dimmed }) => {
  const c = theme.tokens.colors;
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '1rem',
    borderRadius: '0.5rem',
    border: `0.0625rem solid ${hasError ? c['fill-color-alert-soft']! : c['border-color-neutral-soft']!}`,
    background: c['color-neutral-white']!,
    fontFamily: theme.typography.fontFamily,
    ...(hasError ? { boxShadow: `0 0 0 0.25rem ${c['fill-color-alert-softer']!}` } : {}),
    ...(dimmed ? { '& > *': { opacity: 0.5 } } : {}),
  };
});

export const Header = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'layout',
})<{ layout?: TariffCardLayout }>(({ theme, layout }) => ({
  display: 'flex',
  gap: '0.5rem',
  width: '100%',
  ...(layout
    ? headerByLayout(layout)
    : { ...headerByLayout('mobile'), [theme.breakpoints.up('sm')]: headerByLayout('desktop') }),
}));

export const Title = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  flex: 1,
  minWidth: 0,
});

export const Name = styled('span')(({ theme }) => ({
  ...theme.tokens.typography['text-md-semibold'],
  fontFamily: theme.typography.fontFamily,
  color: theme.tokens.colors['text-color-neutral-strongest'],
}));

export const DependentLabel = styled('span')(({ theme }) => ({
  ...theme.tokens.typography['text-sm-regular'],
  fontFamily: theme.typography.fontFamily,
  color: theme.tokens.colors['text-color-neutral-stronger'],
}));

export const PriceQuantityRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'layout',
})<{ layout?: TariffCardLayout }>(({ theme, layout }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  ...(layout
    ? priceRowByLayout(layout)
    : { ...priceRowByLayout('mobile'), [theme.breakpoints.up('sm')]: priceRowByLayout('desktop') }),
}));

export const DescriptionSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  width: '100%',
});

export const Divider = styled('hr')(({ theme }) => ({
  margin: 0,
  border: 0,
  borderTop: `0.0625rem solid ${theme.tokens.colors['border-color-neutral-soft']!}`,
  width: '100%',
}));

export const Description = styled('p')(({ theme }) => ({
  ...theme.tokens.typography['text-sm-regular'],
  fontFamily: theme.typography.fontFamily,
  color: theme.tokens.colors['text-color-neutral-stronger'],
  margin: 0,
  width: '100%',
}));

export const UnavailableNotice = styled('span')(({ theme }) => ({
  ...theme.tokens.typography['text-sm-regular'],
  fontFamily: theme.typography.fontFamily,
  color: theme.tokens.colors['text-color-neutral-stronger'],
}));
